/** @file Export record of a region attachment; a mounted region's placement is carried by its mount. */
import type { ImageDoc, Pose2DJson, RegionAtt } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';
import { handleArray } from './handleArray';

type RegionJson = Extract<Pose2DJson['attachments'][number], { type: 'region' }>;
type Placement = [x: number, y: number, rotation: number, scaleX: number, scaleY: number];

/**
 * Placement of M · fromLocal(x, y, rotation, scaleX, scaleY): M (mountMatrix) is a similarity, so its turn adds to
 * the rotation and its scale multiplies both scales; width, height and the authored scale signs are kept, so the
 * quad draws at its authored size (final review C2/C7, ruling b) exactly where runtime equip would draw it.
 */
function mountedPlacement(att: RegionAtt, m: Affine): Placement {
  const at = affine.apply(m, att.x, att.y, [0, 0]);
  const a = m[0] ?? 1;
  const c = m[2] ?? 0;
  const s = trig.hypot(a, c);
  return [at[0], at[1], angle.wrap180(att.rotation + angle.radToDeg(trig.atan2(c, a))), att.scaleX * s, att.scaleY * s];
}

/** The rig record; `handle` whenever the document has one, `fitLength` only on mounted regions. */
export function regionRecord(att: RegionAtt, image: ImageDoc, mount: Affine | null): RegionJson {
  const p: Placement = mount === null ? [att.x, att.y, att.rotation, att.scaleX, att.scaleY] : mountedPlacement(att, mount);
  return {
    type: 'region', name: att.name, image: image.name, color: att.color,
    x: p[0], y: p[1], rotation: p[2], scaleX: p[3], scaleY: p[4], w: att.width, h: att.height,
    ...(att.handle === undefined ? {} : { handle: handleArray(att.handle) }),
    ...(att.mount === undefined ? {} : { fitLength: att.mount.fitLength }),
  };
}
